import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkErrorHandlerService {
  private logger = inject(LoggerService);

  /**
   * Check if the error is related to browser extensions or should be ignored
   */
  shouldIgnoreError(error: HttpErrorResponse | Error | unknown): boolean {
    // Check for browser extension errors
    if (this.isBrowserExtensionError(error)) {
      return true;
    }

    // Check for network errors that are not critical
    if (error instanceof HttpErrorResponse) {
      return this.isNonCriticalNetworkError(error);
    }

    return false;
  }

  /**
   * Handle network errors with proper logging and user feedback
   */
  handleNetworkError(error: HttpErrorResponse, context?: string): void {
    if (this.shouldIgnoreError(error)) {
      this.logger.debug(`Ignoring non-critical network error: ${error.status}`, {
        url: error.url,
        status: error.status,
        context
      });
      return;
    }

    this.logger.error(`Network error occurred: ${error.status}`, {
      url: error.url,
      status: error.status,
      message: error.message,
      context
    });
  }

  private isBrowserExtensionError(error: unknown): boolean {
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      const errorStack = error.stack?.toLowerCase() || '';

      return (
        errorMessage.includes('content.js') ||
        errorStack.includes('content.js') ||
        errorMessage.includes('chrome-extension') ||
        errorStack.includes('chrome-extension') ||
        errorMessage.includes('chrome-error://') ||
        errorStack.includes('chrome-error://') ||
        errorMessage.includes('moz-extension') ||
        errorStack.includes('moz-extension')
      );
    }

    if (error instanceof HttpErrorResponse) {
      const url = error.url?.toLowerCase() || '';
      return (
        url.includes('chrome-extension://') ||
        url.includes('moz-extension://') ||
        url.includes('chrome-error://')
      );
    }

    return false;
  }

  private isNonCriticalNetworkError(error: HttpErrorResponse): boolean {
    // These status codes are usually not critical for user experience
    const nonCriticalStatuses = [0, 408, 429, 502, 503, 504];

    // Also ignore CORS errors from extensions
    if (error.status === 0 && error.url?.includes('chrome-extension://')) {
      return true;
    }

    return nonCriticalStatuses.includes(error.status);
  }
}
